import { cn } from '@/lib/utils';
import { CareEnvironment, formatCurrency, formatPercent, formatROI, getROILabel } from '@shared';
import { useState } from 'react';
import { BsFillQuestionCircleFill } from 'react-icons/bs';
import AnimatedText from './AnimatedText';
import { LtcCostDisplay } from './LtcCostDisplay';
import { CareAssuranceSavingsSlideOutContent } from './LearnMoreSlideOutContent';
import { SheetModal } from './SheetModal';

export type FundingMetricId = 'ltcCost' | 'ltcCoverage' | 'roi' | 'discount';

type MetricDefinition = {
  metricId: FundingMetricId;
  label: string;
  stat: number;
  formattedStat?: string;
  subStatLabel: string;
  subStat?: number;
  titleColor: string;
  bgColor: string;
  statFormatter?: (value: number) => string;
  disclaimer?: string;
  slideOutContent?: React.ReactNode;
};

type MetricCardProps = Omit<MetricDefinition, 'metricId'> & {
  delay: number;
  showSeparator?: boolean;
  statContent?: React.ReactNode;
  subStatContent?: React.ReactNode;
};

export function MetricCard({
  label,
  formattedStat,
  stat,
  subStatLabel,
  subStat,
  titleColor,
  bgColor,
  statFormatter = formatCurrency,
  delay,
  showSeparator,
  disclaimer,
  slideOutContent,
  statContent,
  subStatContent,
}: MetricCardProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <div className={`relative flex flex-col gap-1 rounded-2xl p-4 sm:p-5 ${bgColor}`}>
      {showSeparator && (
        <div className="bg-primary-200 absolute right-0 top-4 h-[calc(100%-2rem)] w-px" />
      )}
      <dt className="flex items-center gap-1 text-sm font-medium text-gray-700">
        {label}
        {slideOutContent && (
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="hover:text-gray-700"
            aria-label="More information"
          >
            <BsFillQuestionCircleFill className="h-3 w-3 text-gray-500 hover:text-gray-700" />
          </button>
        )}
      </dt>
      <dd className="flex flex-col items-baseline justify-between gap-1 md:block lg:flex">
        <div className={`text-lg font-semibold md:text-xl ${titleColor}`}>
          {statContent ?? (
            <AnimatedText
              key={stat}
              text={formattedStat ?? statFormatter(stat)}
              delay={delay}
            />
          )}
        </div>
        <div className="flex w-full flex-col gap-1">
          <span className="text-xs font-medium text-gray-700">
            {subStatContent !== undefined ? (
              <>
                {subStatLabel}: {subStatContent}
              </>
            ) : subStat === undefined ? (
              subStatLabel
            ) : subStat === Infinity ? (
              `${subStatLabel}: Unlimited`
            ) : (
              `${subStatLabel}: ${formatCurrency(subStat)}`
            )}
          </span>
          {disclaimer && <span className="text-xs italic text-gray-500">{disclaimer}</span>}
        </div>
      </dd>

      {slideOutContent && (
        <SheetModal
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          title="Care Assurance Savings"
          subTitle=""
          modal={true}
          className="min-w-[95%] sm:min-w-[90%] md:min-w-[600px] lg:min-w-[650px]"
        >
          <div className="flex min-h-full flex-1 flex-col overflow-auto text-black">
            <div className="flex flex-1 flex-col py-4">{slideOutContent}</div>
          </div>
        </SheetModal>
      )}
    </div>
  );
}

export const getMetricDefinitions = (client: Client): MetricDefinition[] => {
  // Get client data for metrics
  const {
    allPhaseCosts: {
      allPhaseInflatedProfessionalShareCost,
      allPhaseProfessionalShareCost,
      allPhaseInflatedProfessionalShareCostDiscountAmount,
    },
    fundingSources: {
      combinedProjectedCostCoverage,
      combinedProjectedCostCoveragePercent,
      combinedProjectedReturnOnInvestment,
      combinedProjectedCompoundAnnualGrowthRate,
      combinedProjectedInternalRateOfReturn,
      combinedInvestmentPeriodYears,
      combinedFundingTotalCost,
    },
    multipleFundingSources: { multiplePolicyTotalLtcBenefit },
    clientCalculationSettings,
    clientLocalCareEnvironmentCosts,
  } = client;

  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType;

  const formattedProjectedReturnOnInvestment = formatROI(
    {
      projectedReturnOnInvestment: combinedProjectedReturnOnInvestment ?? 0,
      projectedCompoundAnnualGrowthRate: combinedProjectedCompoundAnnualGrowthRate ?? 0,
      projectedInternalRateOfReturn: combinedProjectedInternalRateOfReturn,
      investmentPeriodYears: combinedInvestmentPeriodYears ?? 0,
    },
    returnOnInvestmentCalculationType,
  );

  const returnOnInvestmentLabel = getROILabel(returnOnInvestmentCalculationType);

  const metrics: MetricDefinition[] = [
    {
      metricId: 'ltcCost',
      label: 'Projected LTC Cost',
      stat: allPhaseInflatedProfessionalShareCost,
      subStatLabel: "In today's dollars",
      subStat: allPhaseProfessionalShareCost,
      titleColor: 'text-rose-500',
      bgColor: 'bg-white',
    },
    {
      metricId: 'ltcCoverage',
      label: 'Projected LTC Coverage',
      stat: combinedProjectedCostCoverage ?? 0,
      formattedStat: `${combinedProjectedCostCoverage ? `${formatCurrency(combinedProjectedCostCoverage)} (${formatPercent(combinedProjectedCostCoveragePercent, 0)})` : '$0'}`,
      subStatLabel: 'Total LTC Benefit',
      subStat: multiplePolicyTotalLtcBenefit ?? 0,
      titleColor: 'text-green-500',
      bgColor: 'bg-white',
    },
  ];

  metrics.push({
    metricId: 'roi',
    label: `Projected ${returnOnInvestmentLabel}`,
    stat: combinedProjectedReturnOnInvestment ?? 0,
    formattedStat: combinedProjectedReturnOnInvestment
      ? formattedProjectedReturnOnInvestment
      : '0%',
    subStatLabel: 'Total invested',
    subStat: combinedFundingTotalCost ?? 0,
    titleColor: 'text-primary',
    bgColor: 'bg-white',
  });

  // Add discount metric only if there's a discount amount
  if (allPhaseInflatedProfessionalShareCostDiscountAmount > 0) {
    // For now, hardcoding to home care environment since this is the only care environment that has a discount
    const homeCareEnvironment = clientLocalCareEnvironmentCosts[CareEnvironment.home];
    const discountInfo = homeCareEnvironment?.discount;

    metrics.push({
      metricId: 'discount',
      label: discountInfo?.label || 'Care Assurance Discount',
      stat: allPhaseInflatedProfessionalShareCostDiscountAmount,
      subStatLabel: discountInfo?.description || 'Savings after inflation',
      titleColor: 'text-blue-600',
      bgColor: 'bg-white',
      disclaimer: 'Savings are not guaranteed. Network availability may change.',
      slideOutContent: <CareAssuranceSavingsSlideOutContent />,
    });
  }

  return metrics;
};

type FundingSourceMetricsProps = {
  client: Client;
  className?: string;
};

function fundingMetricsGridClass(metricCount: number): string {
  if (metricCount >= 4) return 'md:grid-cols-2 lg:grid-cols-4';
  if (metricCount === 3) return 'sm:grid-cols-1 md:grid-cols-3';
  if (metricCount === 2) return 'sm:grid-cols-1 md:grid-cols-2';
  return 'grid-cols-1';
}

function getMetricCardContent(
  client: Client,
  metric: MetricDefinition,
): { statContent?: React.ReactNode; subStatContent?: React.ReactNode } {
  switch (metric.metricId) {
    case 'ltcCost':
      return {
        statContent: <LtcCostDisplay value={metric.stat} />,
        subStatContent:
          metric.subStat != null && metric.subStat !== Infinity
            ? <LtcCostDisplay value={metric.subStat} />
            : undefined,
      };
    case 'ltcCoverage':
      return {
        statContent: (
          <>
            <LtcCostDisplay value={metric.stat} /> (
            {formatPercent(
              client.fundingSources.combinedProjectedCostCoveragePercent ?? 0,
              0,
            )}
            )
          </>
        ),
        subStatContent:
          metric.subStat != null && metric.subStat !== Infinity
            ? <LtcCostDisplay value={metric.subStat} />
            : undefined,
      };
    case 'roi':
      return {
        subStatContent:
          metric.subStat != null && metric.subStat !== Infinity
            ? <LtcCostDisplay value={metric.subStat} />
            : undefined,
      };
    case 'discount':
      return { statContent: <LtcCostDisplay value={metric.stat} /> };
    default:
      return {};
  }
}

export default function FundingSourceMetrics({ client, className }: FundingSourceMetricsProps) {
  const metrics = getMetricDefinitions(client);

  return (
    <div
      className="relative"
      data-testid="funding-metrics-container"
    >
      <dl
        className={cn(
          'border-primary-200 grid grid-cols-1 rounded-2xl border bg-white sm:gap-4',
          fundingMetricsGridClass(metrics.length),
          className,
        )}
      >
        {metrics.map((metric, index) => {
          const { statContent, subStatContent } = getMetricCardContent(client, metric);
          const { metricId, ...metricCardProps } = metric;
          return (
            <MetricCard
              key={metricId}
              {...metricCardProps}
              delay={index * 200}
              showSeparator={index < metrics.length - 1}
              statContent={statContent}
              subStatContent={subStatContent}
            />
          );
        })}
      </dl>
    </div>
  );
}
